import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import VerifiedOrgMark from "./VerifiedOrgMark";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import { API_BASE_URL } from "@/lib/apiBase";
import { useSocialPaths } from "@/lib/socialPaths";
import { toastError, toastSuccess } from "@/lib/toast";
import { cn } from "@/lib/utils";

function Section({ title, seeMoreTo, children, className }) {
  return (
    <section className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between gap-2 min-w-0">
        <h3 className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        {seeMoreTo ? (
          <Link
            to={seeMoreTo}
            className="text-xs text-primary hover:underline shrink-0"
          >
            See more
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/**
 * Desktop sticky discovery rail for Circa Social Home.
 */
export default function SocialHomeRail({ accessToken, className }) {
  const paths = useSocialPaths();
  const [suggestedCommunities, setSuggestedCommunities] = useState([]);
  const [people, setPeople] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const [cRes, uRes, oRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/social/explore/communities`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { limit: 8 },
        }),
        axios.get(`${API_BASE_URL}/social/explore/users`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { limit: 6 },
        }),
        axios.get(`${API_BASE_URL}/social/orgs/explore`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { limit: 6 },
        }),
      ]);

      const communities = (cRes.data?.communities || []).filter(
        (c) => c?.membership?.status !== "active" && c?.access?.can_join
      );
      setSuggestedCommunities(communities.slice(0, 6));
      setPeople(
        (uRes.data?.users || [])
          .filter((u) => !u.is_following && !u.is_self)
          .slice(0, 6)
      );
      setOrgs((oRes.data?.orgs || []).slice(0, 5));
    } catch (err) {
      console.error("Failed to load Social Home rail:", err);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const joinCommunity = async (communityId) => {
    setBusyKey(`join:${communityId}`);
    try {
      await axios.post(
        `${API_BASE_URL}/social/communities/${communityId}/join`,
        {},
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { as_social_visitor: "true" },
        }
      );
      toastSuccess("Joined community");
      await load();
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not join"
      );
    } finally {
      setBusyKey(null);
    }
  };

  const followUser = async (username) => {
    setBusyKey(`user:${username}`);
    try {
      await axios.post(
        `${API_BASE_URL}/social/users/${encodeURIComponent(username)}/follow`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess(`Following @${username}`);
      await load();
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not follow"
      );
    } finally {
      setBusyKey(null);
    }
  };

  const followOrg = async (username) => {
    setBusyKey(`org:${username}`);
    try {
      await axios.post(
        `${API_BASE_URL}/social/orgs/${encodeURIComponent(username)}/follow`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess(`Following @${username}`);
      await load();
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not follow"
      );
    } finally {
      setBusyKey(null);
    }
  };

  if (loading && !suggestedCommunities.length && !people.length && !orgs.length) {
    return (
      <aside
        className={cn(
          "hidden 2xl:block w-[280px] shrink-0 min-w-0 sticky top-4 self-start",
          className
        )}
      >
        <p className="text-xs text-muted-foreground">Loading…</p>
      </aside>
    );
  }

  const hasAnything =
    suggestedCommunities.length > 0 ||
    people.length > 0 ||
    orgs.length > 0;

  if (!hasAnything) return null;

  return (
    <aside
      className={cn(
        "hidden 2xl:block w-[280px] shrink-0 min-w-0 sticky top-4 self-start max-h-[calc(100vh-6rem)] overflow-y-auto overflow-x-hidden space-y-6 pr-1",
        className
      )}
    >
      {suggestedCommunities.length > 0 ? (
        <Section title="Suggested communities" seeMoreTo={paths.explore()}>
          <ul className="space-y-2">
            {suggestedCommunities.map((c) => {
              const href = paths.community(c.slug || c.community_id);
              return (
                <li
                  key={c.community_id}
                  className="flex items-center gap-2 min-w-0"
                >
                  <Link to={href} className="min-w-0 flex-1 hover:underline">
                    <span className="flex items-center gap-1 min-w-0">
                      <span className="truncate text-sm font-medium" title={c.display_name}>
                        {c.display_name}
                      </span>
                      <VerifiedOrgMark
                        org={c}
                        title="Verified community"
                        className="h-3 w-3 shrink-0"
                      />
                    </span>
                    {c.member_count != null ? (
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {c.member_count} members
                      </span>
                    ) : null}
                  </Link>
                  {c.access?.can_join ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs shrink-0"
                      disabled={busyKey === `join:${c.community_id}`}
                      onClick={() => joinCommunity(c.community_id)}
                    >
                      Join
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Section>
      ) : null}

      {people.length > 0 ? (
        <Section title="People to follow" seeMoreTo={paths.explore()}>
          <ul className="space-y-2">
            {people.map((u) => (
              <li key={u.username} className="flex items-center gap-2 min-w-0">
                <Link
                  to={paths.user(u.username)}
                  className="min-w-0 flex-1 hover:underline"
                >
                  <span className="block truncate text-sm font-medium" title={u.display_name}>
                    {u.display_name}
                  </span>
                  <span
                    className="block truncate text-[11px] text-muted-foreground"
                    title={`@${u.username}`}
                  >
                    @{u.username}
                  </span>
                </Link>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs shrink-0"
                  disabled={busyKey === `user:${u.username}`}
                  onClick={() => followUser(u.username)}
                >
                  Follow
                </Button>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {orgs.length > 0 ? (
        <Section title="Verified & active orgs" seeMoreTo={paths.explore()}>
          <ul className="space-y-2">
            {orgs.map((o) => (
              <li key={o.username} className="flex items-center gap-2 min-w-0">
                <OrganizationAvatar
                  org={{
                    name: o.display_name,
                    avatar_url: o.avatar_url,
                  }}
                  className="h-8 w-8 shrink-0"
                  fallbackClassName="text-[10px]"
                />
                <Link
                  to={paths.org(o.username)}
                  className="min-w-0 flex-1 hover:underline"
                >
                  <span className="flex items-center gap-1 min-w-0">
                    <span className="truncate text-sm font-medium" title={o.display_name}>
                      {o.display_name}
                    </span>
                    <VerifiedOrgMark org={o} className="h-3 w-3 shrink-0" />
                  </span>
                  <span
                    className="block truncate text-[11px] text-muted-foreground"
                    title={`@${o.username}`}
                  >
                    @{o.username}
                  </span>
                </Link>
                {!o.is_following ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs shrink-0"
                    disabled={busyKey === `org:${o.username}`}
                    onClick={() => followOrg(o.username)}
                  >
                    Follow
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </aside>
  );
}

/** Slim in-feed discovery strip for mobile + desktop. */
export function SocialHomeDiscoveryStrip({ communities, paths }) {
  const list = (communities || []).slice(0, 3);
  if (!list.length) return null;
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-2 min-w-0 overflow-hidden 2xl:hidden">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Communities you might like
      </p>
      <ul className="flex flex-wrap gap-x-3 gap-y-1 min-w-0">
        {list.map((c) => (
          <li key={c.community_id} className="min-w-0 max-w-full">
            <Link
              to={paths.community(c.slug || c.community_id)}
              className="block max-w-[16rem] truncate text-sm text-primary hover:underline"
              title={c.display_name}
            >
              {c.display_name}
            </Link>
          </li>
        ))}
      </ul>
      <Link
        to={paths.explore()}
        className="text-xs text-muted-foreground hover:underline"
      >
        Explore more
      </Link>
    </div>
  );
}
